import { motion } from 'framer-motion';
import {Sparkles, Star, Calendar, TrendingUp, Heart, Briefcase, Activity, User} from 'lucide-react';
import { Link } from 'react-router-dom';

export function HoroscopeRisingSign() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-16"
      >
        <div className="inline-block mb-4 px-4 py-1 rounded-full bg-purple-600/10 border border-purple-500/30 text-purple-300 text-sm font-medium tracking-wide">
          Complete Guide to Rising Signs
        </div>
        <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6 leading-tight">
          Horoscope Rising Sign: <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-purple-400">Your Daily Cosmic Guide</span>
        </h1>
        <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
          Your horoscope rising sign is the key to personalized daily predictions. Unlike generic Sun sign horoscopes, your Rising Sign horoscope reveals how cosmic events specifically affect your life path, relationships, and daily experiences.
        </p>
      </motion.div>

      {/* What is Horoscope Rising Sign */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <div className="bg-gradient-to-br from-[#131722] to-[#0B0E14] p-8 rounded-2xl border border-white/10">
          <h2 className="text-3xl font-serif font-bold text-white mb-6 flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-[#D4AF37]" />
            What is a Horoscope Rising Sign?
          </h2>
          <div className="space-y-4 text-gray-300 leading-relaxed text-lg">
            <p>
              Your <strong className="text-[#D4AF37]">horoscope rising sign</strong> (also called the Ascendant) is the zodiac sign that was rising on the eastern horizon at the exact moment of your birth. This powerful point in your birth chart determines how planetary transits will affect your daily life.
            </p>
            <p>
              While your Sun sign represents your core identity, your <strong className="text-white">horoscope rising sign</strong> acts as your "cosmic filter"—it colors how you experience daily events, challenges, and opportunities. This is why horoscopes based on your Rising Sign are significantly more accurate than generic Sun sign predictions.
            </p>
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-6 rounded-xl mt-6">
              <p className="text-[#D4AF37] font-semibold mb-2">🎯 Key Insight:</p>
              <p className="text-gray-200">
                Professional astrologers read horoscopes primarily through the <strong>Rising Sign</strong> because it represents your 1st House—the starting point of all astrological houses. This makes Rising Sign horoscopes up to 80% more personalized than Sun sign readings.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Why Your Horoscope Rising Sign Matters for Daily Predictions */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <h2 className="text-3xl font-serif font-bold text-white mb-8 text-center">
          Why Your Horoscope Rising Sign Matters for Daily Predictions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FeatureCard
            icon={<Calendar className="w-10 h-10 text-purple-400" />}
            title="Daily Transit Accuracy"
            description="Your horoscope rising sign determines which house each planet transits, revealing exactly which life area (career, love, health) is activated each day."
          />
          <FeatureCard
            icon={<TrendingUp className="w-10 h-10 text-green-400" />}
            title="Personalized Timing"
            description="Planetary movements affect each rising sign differently. What's a career boost for Aries Rising might be a relationship challenge for Libra Rising."
          />
          <FeatureCard
            icon={<Heart className="w-10 h-10 text-pink-400" />}
            title="Relationship Insights"
            description="Your horoscope rising sign reveals when Venus or Mars transits your 7th House (relationships), timing romantic opportunities or challenges."
          />
          <FeatureCard
            icon={<Activity className="w-10 h-10 text-orange-400" />}
            title="Energy Forecasting"
            description="Track Mars and Saturn transits through your rising sign to predict high-energy periods and times requiring patience and discipline."
          />
        </div>
      </motion.section>

      {/* How to Read Your Horoscope Rising Sign */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 p-8 rounded-2xl border border-purple-500/30">
          <h2 className="text-3xl font-serif font-bold text-white mb-6 flex items-center gap-3">
            <Star className="w-8 h-8 text-purple-400" />
            How to Read Your Horoscope Rising Sign (Step-by-Step)
          </h2>
          <div className="space-y-6">
            <Step
              number={1}
              title="Calculate Your Exact Rising Sign"
              description="You need your exact birth time (within 5 minutes), birth date, and birth location. The Rising Sign changes every 2 hours, so accuracy is critical."
            />
            <Step
              number={2}
              title="Find Rising Sign-Based Horoscopes"
              description="Look for horoscopes that specifically mention 'Read for your Rising Sign' or 'Ascendant-based predictions.' These are written by professional astrologers tracking planetary transits."
            />
            <Step
              number={3}
              title="Compare with Your Sun Sign"
              description="Read both your Sun sign and Rising sign horoscopes. Notice how the Rising Sign version feels more personally relevant to your current life circumstances."
            />
            <Step
              number={4}
              title="Track Planetary Transits"
              description="Pay special attention when planets transit your Rising Sign or your 1st House. These are major 'activation periods' lasting days to months depending on the planet."
            />
          </div>
        </div>
      </motion.section>

      {/* Horoscope Rising Sign vs Sun Sign Horoscope */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <h2 className="text-3xl font-serif font-bold text-white mb-8 text-center">
          Horoscope Rising Sign vs. Sun Sign Horoscope: The Critical Difference
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-[#131722] p-8 rounded-xl border border-[#D4AF37]/50">
            <h3 className="text-xl font-bold text-[#D4AF37] mb-4 flex items-center gap-2">
              <User className="w-6 h-6" />
              Sun Sign Horoscope
            </h3>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-yellow-500 mt-1">•</span>
                <span>Based on birth month only (no time needed)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-500 mt-1">•</span>
                <span>Generic predictions for 1/12 of world population</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-500 mt-1">•</span>
                <span>Describes your core personality and life purpose</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-500 mt-1">•</span>
                <span>Useful for understanding character traits</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400 mt-1">✗</span>
                <span>Not personalized to your exact birth chart</span>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 p-8 rounded-xl border border-purple-500/50 shadow-[0_0_30px_rgba(168,85,247,0.2)]">
            <h3 className="text-xl font-bold text-purple-300 mb-4 flex items-center gap-2">
              <Sparkles className="w-6 h-6" />
              Horoscope Rising Sign
            </h3>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-1">✓</span>
                <span>Requires exact birth time + location (personalized)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-1">✓</span>
                <span>Unique predictions for your specific chart</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-1">✓</span>
                <span>Tracks how transits affect your 12 houses</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-1">✓</span>
                <span>Reveals timing of events in different life areas</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-1">✓</span>
                <span><strong>Recommended by professional astrologers</strong></span>
              </li>
            </ul>
          </div>
        </div>
      </motion.section>

      {/* What Your Horoscope Rising Sign Controls */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <div className="bg-[#131722] p-8 rounded-2xl border border-white/10">
          <h2 className="text-3xl font-serif font-bold text-white mb-6">
            What Your Horoscope Rising Sign Controls in Daily Predictions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <DomainCard
              icon={<Briefcase className="w-8 h-8 text-blue-400" />}
              title="Career & Public Life"
              areas={['Job opportunities', 'Professional reputation', 'Authority figures', 'Career momentum']}
            />
            <DomainCard
              icon={<Heart className="w-8 h-8 text-pink-400" />}
              title="Love & Relationships"
              areas={['Romantic timing', 'Partnership dynamics', 'Social connections', 'Attraction patterns']}
            />
            <DomainCard
              icon={<Activity className="w-8 h-8 text-green-400" />}
              title="Health & Energy"
              areas={['Physical vitality', 'Energy cycles', 'Health alerts', 'Body rhythms']}
            />
          </div>
        </div>
      </motion.section>

      {/* How to Use Your Horoscope Rising Sign Daily */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <div className="bg-gradient-to-br from-[#D4AF37]/10 to-yellow-500/10 p-8 rounded-2xl border border-[#D4AF37]/30">
          <h2 className="text-3xl font-serif font-bold text-white mb-6">
            How to Use Your Horoscope Rising Sign for Daily Guidance
          </h2>
          <div className="space-y-4 text-gray-300 leading-relaxed">
            <p className="text-lg">
              <strong className="text-[#D4AF37]">Morning Ritual:</strong> Read your Rising Sign horoscope each morning to understand which life area is activated. If today's forecast mentions career (10th House), focus your energy on professional tasks.
            </p>
            <p className="text-lg">
              <strong className="text-[#D4AF37]">Weekly Planning:</strong> Note which days have challenging aspects (squares, oppositions) to your Rising Sign. Schedule important meetings or launches during harmonious transits (trines, sextiles).
            </p>
            <p className="text-lg">
              <strong className="text-[#D4AF37]">Monthly Review:</strong> Track patterns in your horoscope rising sign predictions. If relationship themes repeat monthly, consider this a cosmic signal to address partnership dynamics.
            </p>
            <p className="text-lg">
              <strong className="text-[#D4AF37]">Transit Alerts:</strong> When Saturn, Jupiter, or outer planets transit your Rising Sign (happens rarely), expect major life theme changes lasting 1-2 years.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Common Misconceptions */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <h2 className="text-3xl font-serif font-bold text-white mb-8 text-center">
          Common Misconceptions About Horoscope Rising Sign
        </h2>
        <div className="space-y-4">
          <MythCard
            myth="My Sun sign horoscope is accurate enough"
            reality="Sun sign horoscopes use 'solar houses' which don't match your actual birth chart. Rising Sign horoscopes track real planetary transits through YOUR specific house system."
          />
          <MythCard
            myth="I don't know my exact birth time, so I can't use Rising Sign horoscopes"
            reality="If your birth time is unknown, you can use a noon chart (12:00 PM) as an approximation, or contact your birth hospital for records. Many places keep birth time records for decades."
          />
          <MythCard
            myth="Horoscope rising signs change the meaning of my Sun sign"
            reality="Your Sun sign remains your core identity. The Rising Sign adds context about HOW you experience life events and which life areas are emphasized at different times."
          />
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-center"
      >
        <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 p-10 rounded-2xl border border-purple-500/50">
          <h2 className="text-3xl font-serif font-bold text-white mb-4">
            Ready to Unlock Your Personalized Horoscope?
          </h2>
          <p className="text-gray-300 mb-8 text-lg max-w-2xl mx-auto">
            Calculate your exact Rising Sign now and start receiving daily horoscope predictions tailored to your unique birth chart.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="px-8 py-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0B0E14] font-bold rounded-lg hover:scale-105 transition-transform"
            >
              Calculate My Rising Sign →
            </Link>
            <Link
              to="/daily-horoscope"
              className="px-8 py-4 bg-purple-600/20 border border-purple-500/50 text-purple-300 font-bold rounded-lg hover:bg-purple-600/30 transition-colors"
            >
              View Daily Horoscope
            </Link>
          </div>
        </div>
      </motion.section>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) {
  return (
    <div className="bg-[#131722] p-6 rounded-xl border border-white/10 hover:border-[#D4AF37]/30 transition-all group">
      <div className="mb-4 group-hover:scale-110 transition-transform">{icon}</div>
      <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

function Step({ number, title, description }: { number: number, title: string, description: string }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center font-bold text-white">
        {number}
      </div>
      <div>
        <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
        <p className="text-gray-300 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function DomainCard({ icon, title, areas }: { icon: React.ReactNode, title: string, areas: string[] }) {
  return (
    <div className="bg-white/5 p-6 rounded-xl">
      <div className="mb-4">{icon}</div>
      <h3 className="text-lg font-bold text-white mb-3">{title}</h3>
      <ul className="space-y-2">
        {areas.map((area, idx) => (
          <li key={idx} className="text-gray-400 text-sm flex items-center gap-2">
            <span className="text-[#D4AF37]">•</span>
            {area}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MythCard({ myth, reality }: { myth: string, reality: string }) {
  return (
    <div className="bg-[#131722] p-6 rounded-xl border border-white/10">
      <div className="flex items-start gap-3">
        <span className="text-red-400 text-2xl">✗</span>
        <div>
          <p className="text-white font-semibold mb-2">Myth: {myth}</p>
          <div className="flex items-start gap-3">
            <span className="text-green-400 text-2xl">✓</span>
            <p className="text-gray-300">Reality: {reality}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
